/* eslint-disable react/jsx-no-target-blank */
/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState, useEffect} from 'react'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import {registerWithEmailAndPassword} from '../core/_requests'
import {Link} from 'react-router-dom'
import {signInWithPopup, updateProfile} from 'firebase/auth'
import {PasswordMeterComponent} from '../../../../_metronic/assets/ts/components'
import {useAuth} from '../core/Auth'
import {v4 as uuid} from 'uuid'
import {Helmet} from 'react-helmet'
import posthog from 'posthog-js'

import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  setDoc,
  writeBatch,
  query,
  where,
} from '@firebase/firestore'
import db, {auth1, googleProvider} from '../../../../services/firebase'
import {toAbsoluteUrl} from '_metronic/helpers'
import {restrictedDomains} from '../core/restrictedDomain'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import {ToastContainer} from 'react-toastify'
import {handleAuthError} from 'app/modules/auth/helpers/authErrorHandler'
import { createActivityLog } from '../helpers/authHelpers'

const initialValues = {
  firstname: '',
  lastname: '',
  email: '',
  emailVerificationCode: '',
  password: '',
  acceptTerms: true,
}

const checkPasswordStrength = (password: string) => {
  const checks = {
    length: password.length >= 8,
    letters: /[a-zA-Z]/.test(password),
    numbers: /[0-9]/.test(password),
    symbols: /[^a-zA-Z0-9]/.test(password),
  }

  return {
    valid: Object.values(checks).every(Boolean),
  }
}

const registrationSchema = Yup.object().shape({
  firstname: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('First name is required'),
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required')
    .test('is-work-email', 'Please use your company work email', (value) => {
      if (!value) return false
      const domain = value.split('@')[1]?.toLowerCase()
      if (!domain) return false
      return !restrictedDomains.includes(domain)
    }),
  emailVerificationCode: Yup.string()
    .matches(/^\d{6}$/, 'Verification code must be 6 digits')
    .required('Verification code is required'),
  lastname: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Last name is required'),
  password: Yup.string()
    .required('Password is required')
    .test(
      'password-strength',
      'Password must contain at least 8 characters with letters, numbers, and symbols',
      (value) => {
        if (!value) return false
        const result = checkPasswordStrength(value)
        return result.valid
      }
    ),
  acceptTerms: Yup.bool().required('You must accept the terms and conditions'),
})
const generateUniqueTenantId = () => {
  return uuid()
}

export function TrialRegistration() {
  const [loading, setLoading] = useState(false)
  const {saveAuth, setCurrentUser} = useAuth()
  const [usersData, setUsersData] = useState<any[]>([])
  const [usersDataLoading, setUsersDataLoading] = useState(true)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [googleError, setGoogleError] = useState<string | null>(null)
  // const [githubLoading, setGithubLoading] = useState(false)
  // const [githubError, setGithubError] = useState<string | null>(null)
  const [currentStep, setCurrentStep] = useState(1)

  useEffect(() => {
    async function fetchUsersData() {
      const usersCollectionRef = collection(db, 'users')
      try {
        const usersSnapshot = await getDocs(usersCollectionRef)
        const usersList = usersSnapshot.docs.map((doc) => doc.data())
        setUsersData(usersList)
      } catch (error) {
        console.error('Error fetching users data:', error)
      } finally {
        setUsersDataLoading(false)
      }
    }

    fetchUsersData()
  }, [])

  const formik = useFormik({
    initialValues,
    validationSchema: registrationSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        const db = getFirestore()

        // Step 1: Register the new user
        const authCredential = await registerWithEmailAndPassword(
          values.email,
          values.password,
          values.firstname,
          values.lastname,
          '' // empty string for confirmPassword
          // values.changepassword,
        )

        if (!authCredential.user || !authCredential.user.uid) {
          throw new Error('User is not authenticated')
        }

        // const isEmailUsed = usersData.some((user) => user.email === values.email)
        // if (isEmailUsed) {
        //   setStatus('Email is already in use.')
        //   setSubmitting(false)
        //   setLoading(false)
        //   return
        // }

        const userDocRef = doc(db, 'users', authCredential.user.uid)
        const userDocSnap = await getDoc(userDocRef)

        let userModel
        const trialStartDate = new Date()
        const trialEndDate = new Date(trialStartDate)
        trialEndDate.setDate(trialEndDate.getDate() + 7)

        if (!userDocSnap.exists()) {
          const userToken = await authCredential.user.getIdToken()
          const tenantId = generateUniqueTenantId()

          // Step 4: Update profile with first and last name
          await updateProfile(authCredential.user, {
            displayName: `${values.firstname} ${values.lastname}`,
          })

          // Step 5: Set up auth and user models
          const authModel = {
            api_token: userToken,
            email: authCredential.user.email,
            uid: authCredential.user.uid,
            userName: `${values.firstname} ${values.lastname}`,
            password: values.password,
            first_name: values.firstname,
            last_name: values.lastname,
            tenantId: tenantId,
          }

          userModel = {
            uid: authModel.uid,
            userName: authModel.userName,
            api_token: authModel.api_token,
            password: authModel.password,
            email: authModel.email,
            first_name: authModel.first_name,
            last_name: authModel.last_name,
            tenantId: tenantId,
            role: 'Owner',
            account_type: 'Trial',
            isNewUser: true,
            onboardingComplete: false,
          }

          await setDoc(doc(db, 'users', authCredential.user.uid), userModel)

          saveAuth(authModel)
          setCurrentUser(userModel)

          const formattedDate = new Date().toISOString().split('T')[0]
          setupTenantDataInBackground(authCredential.user, tenantId, userModel, formattedDate, trialStartDate, trialEndDate)

          return userModel
        } else {
          userModel = userDocSnap.data()
          window.location.href = '/dashboard'
        }
      } catch (error) {
        console.error('Registration failed:', error)
        saveAuth(undefined)
        setStatus('The registration details are incorrect')
      } finally {
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  const signInWithGoogle = async () => {
    try {
      setGoogleError(null)
      const authCredential = await signInWithPopup(auth1, googleProvider);
      const user = authCredential.user;

      if (!user) {
        throw new Error("Google authentication failed");
      }

      posthog.capture('trial_registration_email_entered', {
        trial_email: user.email,
      })

      const usersCollectionRef = collection(db, "users");
      const emailQuery = query(usersCollectionRef, where("email", "==", user.email ));
      const emailQuerySnapshot = await getDocs(emailQuery);
      if (!emailQuerySnapshot.empty) {
        setGoogleError("Email is already in use.");
        throw new Error("Email is already in use.");
      }

      const userDocRef = doc(db, "users", user.uid);
      const userDocSnap = await getDoc(userDocRef);

      let userModel;
      if (!userDocSnap.exists()) {
        const tenantId = generateUniqueTenantId();

        const trialStartDate = new Date();
        const trialEndDate = new Date(trialStartDate);
        trialEndDate.setDate(trialEndDate.getDate() + 7);
        const formattedDate = trialStartDate.toISOString().split('T')[0];

        userModel = {
          uid: user.uid,
          userName: user.displayName || "",
          api_token: await user.getIdToken(),
          password: "", 
          email: user.email,
          first_name: user.displayName?.split(" ")[0] || "",
          last_name: user.displayName?.split(" ")[1] || "",
          tenantId: tenantId,
          role: "Owner",
          account_type: "Trial",
          isNewUser: true,
          onboardingComplete: false,
          photo_url: user.photoURL || "",
          provider: "google",
          trial_start_date: trialStartDate.toISOString(),
          trial_end_date: trialEndDate.toISOString(),
        };

        await setDoc(userDocRef, userModel);

        const authModel = {
          api_token: await user.getIdToken(),
          email: user.email,
          uid: user.uid,
          userName: user.displayName || "",
          password: "", 
          first_name: user.displayName?.split(" ")[0].trim() || "",
          last_name: user.displayName?.split(" ")[1].trim() || "",
          tenantId: userModel.tenantId,
          photo_url: user.photoURL || "",
        };

        saveAuth(authModel);
        setCurrentUser(userModel);

        setupTenantDataInBackground(user, tenantId, userModel, formattedDate, trialStartDate, trialEndDate);

        return userModel;
      } else {
        userModel = userDocSnap.data();
      }

      const authModel = {
        api_token: await user.getIdToken(),
        email: user.email,
        uid: user.uid,
        userName: user.displayName || "",
        password: "", 
        first_name: user.displayName?.split(" ")[0].trim() || "",
        last_name: user.displayName?.split(" ")[1].trim() || "",
        tenantId: userModel.tenantId,
        photo_url: user.photoURL || "",
      };

      saveAuth(authModel);
      setCurrentUser(userModel);

      return userModel;
    } catch (error) {
      console.error("Google Sign-In Error:", error);
      saveAuth(undefined);
      throw error;
    }
  };

  const setupTenantDataInBackground = async (
    user: any, 
    tenantId: string, 
    userModel: any, 
    formattedDate: string, 
    trialStartDate: Date, 
    trialEndDate: Date
  ) => {
    try {
      const batch = writeBatch(db);
      const tenantRef = doc(db, "tenants", tenantId);

      batch.set(tenantRef, {});

      const controlsSnapshot = await getDocs(collection(db, "default_controls"));
      controlsSnapshot.forEach((controlDoc) => {
        batch.set(doc(collection(tenantRef, "controls"), controlDoc.id), controlDoc.data());
      });

      const testsSnapshot = await getDocs(collection(db, "default_tests"));
      testsSnapshot.forEach((testDoc) => {
        batch.set(doc(collection(tenantRef, "tests"), testDoc.id), testDoc.data());
      });

      const policySnapshot = await getDocs(collection(db, "policy_templates"));
      policySnapshot.forEach((policyDoc) => {
        batch.set(doc(collection(tenantRef, "policies"), policyDoc.id), policyDoc.data());
      });

      const groupRef = doc(collection(tenantRef, "personnel_group"));
      batch.set(groupRef, {
        groupName: "[Default]",
        checklist: "[Default]",
        createdOn: formattedDate,
        createdBy: userModel.userName,
        tenantId: tenantId,
      });

      const checklistRef = doc(collection(tenantRef, "personnel_checklist"));
      batch.set(checklistRef, {
        checklist_name: "[Default]",
        policies: [],
        createdon: formattedDate,
        createdBy: userModel.userName,
        tenantId: tenantId,
      });

      const personnelRef = doc(collection(tenantRef, "employees"), user.uid);
      batch.set(personnelRef, {
        created_at: new Date().toISOString(),
        tenantId: tenantId,
        uid: user.uid,
        full_name: userModel.userName,
        group: "[Default]",
        role: "Owner",
        start_date: formattedDate,
        email: userModel.email,
        account_type: "Trial",
        image_url: userModel.photo_url || userModel.image_url || "",
        first_name: userModel.first_name,
        last_name: userModel.last_name,
        trial_start_date: trialStartDate.toISOString(),
        trial_end_date: trialEndDate.toISOString(),
      });

      await createActivityLog(user, tenantId, userModel);

      await batch.commit();
    } catch (error) {
      console.error("Background tenant setup failed:", error);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true); 
    setGoogleError(null);
    try {
      await signInWithGoogle();
    } catch (error: any) {
      setGoogleError(handleAuthError(error, 'Google'));
    } finally {
      setGoogleLoading(false); 
    }
  };

  // const signInWithGithub = async () => {
  //   try {
  //     setGithubError(null);
  //     const authCredential = await signInWithPopup(auth1, githubProvider);
  //     const user = authCredential.user;
  //     if (!user) {
  //       throw new Error("GitHub authentication failed");
  //     }

  //     posthog.capture('trial_registration_email_entered', {
  //       trial_email: user.email,
  //     })

  //     const usersCollectionRef = collection(db, "users");
  //     const emailQuery = query(usersCollectionRef, where("email", "==", user.email));
  //     const emailQuerySnapshot = await getDocs(emailQuery);
  //     if (!emailQuerySnapshot.empty) {
  //       setGithubError("Email is already in use.");
  //       throw new Error("Email is already in use.");
  //     }

  //     const userDocRef = doc(db, "users", user.uid);
  //     const userDocSnap = await getDoc(userDocRef);

  //     let userModel;
  //     const displayName = user.displayName || "";
  //     const nameParts = displayName.split(" ");
  //     const firstName = nameParts[0] || "";
  //     const lastName = nameParts.slice(1).join(" ") || ""; 
      
  //     if (!userDocSnap.exists()) {
  //       const tenantId = generateUniqueTenantId();
  //       const trialStartDate = new Date();
  //       const trialEndDate = new Date(trialStartDate);
  //       trialEndDate.setDate(trialEndDate.getDate() + 7);
  //       const formattedDate = trialStartDate.toISOString().split('T')[0];

  //       userModel = {
  //         uid: user.uid,
  //         userName: displayName,
  //         api_token: await user.getIdToken(),
  //         password: "", 
  //         email: user.email || "",
  //         first_name: firstName,
  //         last_name: lastName,
  //         tenantId: tenantId,
  //         role: "Owner",
  //         account_type: "Trial",
  //         isNewUser: true,
  //         onboardingComplete: false,
  //         photo_url: user.photoURL || "",
  //         provider: "github",
  //         trial_start_date: trialStartDate.toISOString(),
  //         trial_end_date: trialEndDate.toISOString(),
  //       };

  //       await setDoc(userDocRef, userModel);

  //       const authModel = {
  //         api_token: await user.getIdToken(),
  //         email: user.email || "",
  //         uid: user.uid,
  //         userName: user.displayName || "",
  //         password: "", 
  //         first_name: firstName || "",
  //         last_name: lastName || "",
  //         tenantId: userModel.tenantId,
  //         photo_url: user.photoURL || "",
  //       };

  //       saveAuth(authModel);
  //       setCurrentUser(userModel);

  //       setupTenantDataInBackground(user, tenantId, userModel, formattedDate, trialStartDate, trialEndDate);

  //       return userModel;
  //     } else {
  //       userModel = userDocSnap.data();
  //     }

  //     const authModel = {
  //       api_token: await user.getIdToken(),
  //       email: user.email || "",
  //       uid: user.uid,
  //       userName: user.displayName || "",
  //       password: "", 
  //       first_name: firstName || "",
  //       last_name: lastName || "",
  //       tenantId: userModel.tenantId,
  //       photo_url: user.photoURL || "",
  //     };

  //     saveAuth(authModel);
  //     setCurrentUser(userModel);

  //     return userModel;
  //   } catch (error: any) {
  //     console.error("GitHub Sign-In Error:", error);
  //     saveAuth(undefined);
  //     throw error;
  //   }
  // };

  //   const handleGithubSignIn = async () => {
  //   setGithubLoading(true);
  //   setGithubError(null);
  //   try {
  //     const user = await signInWithGithub();
  //   } catch (error: any) {
  //     setGithubError(handleAuthError(error, 'GitHub'));
  //   } finally {
  //     setGithubLoading(false); 
  //   }
  // };

  useEffect(() => {
    PasswordMeterComponent.bootstrap()
  }, [])

  useEffect(() => {
    if (formik.status) {
      window.scrollTo({top: 0, behavior: 'smooth'})
    }
  }, [formik.status])

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className='d-flex flex-column justify-content-center'>
            <h1 className='text-dark fw-bolder mb-3'>Start your free 7-day trial</h1>

            <p className='text-gray-700 fw-semibold fs-6'>
              No credit-card required to start. Access to premium features.
            </p>
            <div className='mt-4'>
            <a
              className='btn btn-flex btn-outline btn-text-gray-700 btn-active-color-dark bg-state-light flex-center text-nowrap w-100 mb-3'
              onClick={handleGoogleSignIn}
            >
              <img
                alt='Logo'
                src={toAbsoluteUrl('/media/svg/brand-logos/google-icon.svg')}
                className='h-15px me-3'
              />
              {!googleLoading && <span className='indicator-label'>Sign up with Google</span>}
              {googleLoading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Signing in...
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </a>
            {googleError && (
              <div className='error-message alert alert-danger mt-2'>
                <div className='alert-text font-weight-bold'>{googleError}</div>
              </div>
            )}

            {/* <a
              className='btn btn-flex btn-outline btn-text-gray-700 btn-active-color-dark bg-state-light flex-center text-nowrap w-100'
              onClick={handleGithubSignIn}
            >
              <img
                alt='Logo'
                src={toAbsoluteUrl('/media/svg/brand-logos/github-1.svg')}
                className='h-15px me-3'
              />
              {!githubLoading && <span className='indicator-label'>Sign up with GitHub</span>}
              {githubLoading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Signing in...
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </a>
            {githubError && (
              <div className='error-message alert alert-danger mt-2'>
                <div className='alert-text font-weight-bold'>{githubError}</div>
              </div>
            )} */}
          </div> 

           <div className='separator separator-content my-10'>
          <span className='w-125px text-gray-700 fw-semibold fs-7'>Or with email</span>
        </div>

            <div className='d-flex flex-column justify-content-center gap-4 '>
              {formik.status && (
                <div className='error-message alert alert-danger'>
                  <div className='alert-text font-weight-bold'>{formik.status}</div>
                </div>
              )}
              <div className=''>
                <label className='form-label fw-semibold text-dark fs-6'>Email</label>
                <input
                  placeholder='Email'
                  type='email'
                  autoComplete='off'
                  {...formik.getFieldProps('email')}
                  onChange={e => {
                    formik.handleChange(e)
                    if (formik.status) formik.setStatus('')
                  }}
                  className={clsx(
                    'form-control bg-transparent',
                    {'is-invalid': formik.touched.email && formik.errors.email},
                    {
                      'is-valid': formik.touched.email && !formik.errors.email,
                    }
                  )}
                />
                {formik.touched.email && formik.errors.email && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.email}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className=''>
                {/* <label className='form-check form-check-inline' htmlFor='kt_login_toc_agree'> */}
                {/* <input
                  className='form-check-input'
                  type='checkbox'
                  id='kt_login_toc_agree'
                  {...formik.getFieldProps('acceptTerms')}
                /> */}
                <span className='fs-7'>
                  By clicking "Continue" you agree to SecureSlate's
                  <a
                    href='https://getsecureslate.com/policy#terms'
                    target='_blank'
                    className='ms-1 link-primary fw-semibold'
                  >
                    Terms & Conditions
                  </a>{' '}
                  and
                  <a
                    href='https://getsecureslate.com/policy#privacy'
                    target='_blank'
                    className='ms-1 link-primary fw-semibold'
                  >
                    Privacy Policy
                  </a>
                  .
                </span>
                {/* </label> */}
                {/* {formik.touched.acceptTerms && formik.errors.acceptTerms && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.acceptTerms}</span>
                  </div>
                </div>
              )} */}
              </div>
            </div>
          </div>
        )
      case 2:
        return (
          <div className='d-flex flex-column justify-content-center gap-4'>
            {formik.status && (
              <div className='error-message alert alert-danger'>
                <div className='alert-text font-weight-bold'>{formik.status}</div>
              </div>
            )}

            <div className=''>
              <label className='form-label fw-semibold text-dark fs-6'>Verification Code</label>
              <div className='position-relative mb-3'>
                <input
                  type='text'
                  placeholder='Verification Code'
                  autoComplete='off'
                  onInput={(e) => {
                    e.currentTarget.value = e.currentTarget.value.replace(/[^0-9]/g, '')
                  }}
                  {...formik.getFieldProps('emailVerificationCode')}
                  className={clsx(
                    'form-control bg-transparent',
                    {
                      'is-invalid':
                        formik.touched.emailVerificationCode && formik.errors.emailVerificationCode,
                    },
                    {
                      'is-valid':
                        formik.touched.emailVerificationCode &&
                        !formik.errors.emailVerificationCode,
                    }
                  )}
                />
                {formik.touched.emailVerificationCode && formik.errors.emailVerificationCode && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.emailVerificationCode}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      case 3:
        return (
          <div className='' data-kt-password-meter='true'>
            <label className='form-label fw-semibold text-dark fs-6'>Password</label>
            <div className='position-relative mb-3'>
              <input
                type='password'
                placeholder='Password'
                autoComplete='off'
                {...formik.getFieldProps('password')}
                className={clsx(
                  'form-control bg-transparent',
                  {
                    'is-invalid': formik.touched.password && formik.errors.password,
                  },
                  {
                    'is-valid': formik.touched.password && !formik.errors.password,
                  }
                )}
              />
              {formik.touched.password && formik.errors.password && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.password}</span>
                  </div>
                </div>
              )}
            </div>

            <div className='text-gray-700 fs-7'>
              Use 8 or more characters with a mix of letters, numbers & symbols.
            </div>
          </div>
        )
      case 4:
        return (
          <div className='d-flex flex-column justify-content-center gap-4'>
            {formik.status && (
              <div className='error-message alert alert-danger'>
                <div className='alert-text font-weight-bold'>{formik.status}</div>
              </div>
            )}

            <div className='d-flex justify-content-between gap-4'>
              <div className='flex-grow-1'>
                <label className='form-label fw-semibold text-dark fs-6'>First name</label>
                <input
                  placeholder='First name'
                  type='text'
                  autoComplete='off'
                  {...formik.getFieldProps('firstname')}
                  className={clsx(
                    'form-control bg-transparent',
                    {
                      'is-invalid': formik.touched.firstname && formik.errors.firstname,
                    },
                    {
                      'is-valid': formik.touched.firstname && !formik.errors.firstname,
                    }
                  )}
                />
                {formik.touched.firstname && formik.errors.firstname && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.firstname}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className='flex-grow-1'>
                <label className='form-label fw-semibold text-dark fs-6'>Last name</label>
                <input
                  placeholder='Last name'
                  type='text'
                  autoComplete='off'
                  {...formik.getFieldProps('lastname')}
                  className={clsx(
                    'form-control bg-transparent',
                    {
                      'is-invalid': formik.touched.lastname && formik.errors.lastname,
                    },
                    {
                      'is-valid': formik.touched.lastname && !formik.errors.lastname,
                    }
                  )}
                />
                {formik.touched.lastname && formik.errors.lastname && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.lastname}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
    }
  }

  const handleContinue = async () => {
    if (currentStep === 1) {
      if (formik.values.email) {
        const isEmailUsed = usersData.some(
          (user) => user.email?.toLowerCase() === formik.values.email.toLowerCase()
        )
        if (isEmailUsed) {
          formik.setStatus('Email is already in use.')
          return
        }
        posthog.capture('trial_registration_email_entered', {
          trial_email: formik.values.email,
        })

        try {
          setLoading(true)
          const response = await fetch(
            'https://us-central1-slate-134a0.cloudfunctions.net/sendEmailVerificationCode',
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                email: formik.values.email,
              }),
            }
          )

          if (response.ok) {
            showSuccessToast({message: 'Verification code has been sent to your email'})
          } else {
            showErrorToast({message: 'Error while sending verification code to your email'})
            return
          }
        } catch (error) {
          console.error(`Error while sending verification code for email`, error)
        } finally {
          setLoading(false)
        }
      }
    }

    if (currentStep === 2) {
      try {
        formik.setStatus('')
        setLoading(true)
        const response = await fetch(
          'https://us-central1-slate-134a0.cloudfunctions.net/verifyEmailVerificationCode',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: formik.values.email,
              code: formik.values.emailVerificationCode,
            }),
          }
        )

        if (!response.ok) {
          formik.setStatus('Invalid verification code. Please check your mail.')
          return
        }
      } catch (error) {
        console.error(`Error while sending verification code for email`, error)
      } finally {
        setLoading(false)
      }
    }

    if (currentStep === 4) {
      formik.submitForm()
      return
    }
    setCurrentStep((prev) => prev + 1)
  }

  const isContinueDisabled = (): boolean => {
    switch (currentStep) {
      case 1:
        return Boolean(!formik.values.email || formik.errors.email || usersDataLoading)
      case 2:
        return Boolean(!formik.values.emailVerificationCode || formik.errors.emailVerificationCode)
      case 3:
        return Boolean(!formik.values.password || formik.errors.password)
      case 4:
        return Boolean(
          !formik.values.firstname ||
            !formik.values.lastname ||
            formik.errors.firstname ||
            formik.errors.lastname
        )
      default:
        return false
    }
  }

  return (
    <>
      {' '}
      <Helmet>
        <title>SecureSlate</title>
      </Helmet>
      {/* <div className='d-inline-flex bg-gray-200 p-2 rounded-2 align-self-start'>
        <img
          alt='Logo'
          src={toAbsoluteUrl('/Secureslate_logos/Secureslate_dark.png')}
          className='h-20px app-sidebar-logo-default'
        />
      </div> */}
      <div className='d-flex flex-column flex-lg-row flex-column-fluid h-100'>
        {/* begin::Body */}
        <div
          className='d-flex flex-column flex-lg-row-fluid w-lg-50 p-5 order-2 order-lg-1 '
          style={{maxHeight: '100vh', overflowY: 'auto'}}
        >
          {/* begin::Form */}
          <div className='d-flex flex-center flex-column flex-lg-row-fluid'>
            {/* <a href='/' className='form-logo w-inline-block mb-4'>
              {' '}
              <img
                alt='Logo'
                src={toAbsoluteUrl('/Secureslate_logos/Secureslate_dark.png')}
                className='h-40px'
              />
            </a> */}

            {/* begin::Wrapper */}
            <div className='w-lg-600px '>
              <form
                className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
                noValidate
                id='kt_login_signup_form'
                onSubmit={formik.handleSubmit}
              >
                {/* begin::Heading */}
                <div className='p-lg-5 my-8'>
                  <div className='d-inline-flex bg-gray-200 p-2 rounded-2 align-self-start mb-12'>
                    <img
                      alt='Logo'
                      src={toAbsoluteUrl('/Secureslate_logos/Secureslate_dark.png')}
                      className='h-20px app-sidebar-logo-default'
                    />
                  </div>
                  {renderStep()}
                  {/* begin::Title */}
                  {/* <h1 className='text-dark fw-bolder mb-3'>Start your free 7-day trial</h1> */}
                  {/* end::Title */}

                  {/* <div className='text-gray-700 fw-semibold fs-6'>
            {' '}
            No credit-card required to start. Access to premium features and integrations.
          </div> */}

                  <div className='text-center mt-4'>
                    <button
                      type='submit'
                      id='kt_sign_up_submit'
                      className='btn btn-primary w-100 mb-4 fs-6'
                      // className='btn btn-lg btn-primary w-100 mb-4'
                      // style={{
                      //   background: '#0E9F6E',
                      //   borderRadius: '8px',
                      //   color: 'white',
                      //   transition: 'all 0.3s ease',
                      // }}
                      // onMouseOver={(e) => {
                      //   e.currentTarget.style.backgroundColor = '#0b8f61'
                      // }}
                      // onMouseOut={(e) => {
                      //   e.currentTarget.style.backgroundColor = '#0E9F6E'
                      // }}
                      onClick={(e) => {
                        e.preventDefault()
                        handleContinue()
                      }}
                      disabled={isContinueDisabled() || loading}
                    >
                      {currentStep === 4 ? (
                        loading ? (
                          <>
                            <span
                              className='spinner-border spinner-border-sm me-2'
                              role='status'
                              aria-hidden='true'
                            ></span>
                            Getting Started...
                          </>
                        ) : (
                          'Get Started'
                        )
                      ) : loading ? (
                        <>
                          <span
                            className='spinner-border spinner-border-sm me-2'
                            role='status'
                            aria-hidden='true'
                          ></span>
                          Processing...
                        </>
                      ) : (
                        <span className=''>Continue</span>
                      )}
                      {/* {!loading && <span className='indicator-label'>Continue</span>}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Please wait...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )} */}
                    </button>
                    {currentStep === 1 && (
                      <span className='fs-6 fw-medium text-gray-700'>
                        Already have an account?
                        <Link to='/auth/login'>{''} Login</Link>
                      </span>
                    )}
                  </div>
                </div>
                {/* end::Heading */}
                {/* begin::Login options */}
                {/* <div className='justify-content-center  row g-3 mb-9'> */}
                {/* <div className='col-md-12'>
            <button
              onClick={handleGoogleSignIn}
              type='button'
              className='btn btn-flex btn-sm btn-outline btn-text-gray-700 btn-active-color-dark bg-state-light flex-center text-nowrap w-100'
            >
              <img
                alt='Logo'
                src={toAbsoluteUrl('/media/svg/brand-logos/google-icon.svg')}
                className='h-15px me-3'
              />
              {!googleLoading && <span className='indicator-label fs-6'>Continue with Google</span>}
              {googleLoading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Signing up...
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
            {errorMessage && (
              <div className='error-message mb-lg-15 alert alert-danger'>
                <div className='alert-text font-weight-bold'>{errorMessage}</div>
              </div>
            )}
          </div> */}
                {/* end::Col

          {/* begin::Col */}
                {/* <div className='col-md-6'>
          <a
            href='#'
            className='btn btn-flex btn-outline btn-text-gray-700 btn-active-color-primary bg-state-light flex-center text-nowrap w-100'
          >
            <img
              alt='Logo'
              src={toAbsoluteUrl('/media/svg/brand-logos/apple-black.svg')}
              className='theme-light-show h-15px me-3'
            />
            <img
              alt='Logo'
              src={toAbsoluteUrl('/media/svg/brand-logos/apple-black-dark.svg')}
              className='theme-dark-show h-15px me-3'
            />
            Sign in with Apple
          </a>
        </div> */}
                {/* </div> */}
                {/* end::Login options */}
                {/* <div className='separator separator-content my-6'>
          <span className='w-10px text-gray-700 fw-semibold fs-7'>OR</span>
        </div> */}
                {/* {formik.status && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{formik.status}</div>
        </div>
      )} */}
                {/* begin::Form group Firstname */}
                {/* <div className='row gx-4'>
          <div className='col-lg-6'>
            <div className='fv-row mb-5'>
              <label className='form-label fw-semibold text-dark fs-6'>First name</label>
              <input
                placeholder='First name'
                type='text'
                autoComplete='off'
                {...formik.getFieldProps('firstname')}
                className={clsx(
                  'form-control form-control-sm bg-transparent',
                  {
                    'is-invalid': formik.touched.firstname && formik.errors.firstname,
                  },
                  {
                    'is-valid': formik.touched.firstname && !formik.errors.firstname,
                  }
                )}
              />
              {formik.touched.firstname && formik.errors.firstname && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.firstname}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className='col-lg-6'>
            <div className='fv-row mb-5'>
              <label className='form-label fw-semibold text-dark fs-6'>Last name</label>
              <input
                placeholder='Last name'
                type='text'
                autoComplete='off'
                {...formik.getFieldProps('lastname')}
                className={clsx(
                  'form-control form-control-sm bg-transparent',
                  {
                    'is-invalid': formik.touched.lastname && formik.errors.lastname,
                  },
                  {
                    'is-valid': formik.touched.lastname && !formik.errors.lastname,
                  }
                )}
              />
              {formik.touched.lastname && formik.errors.lastname && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.lastname}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div> */}
                {/* end::Form group */}
                {/* <div className='fv-row mb-5'>
          <label className='form-label fw-bolder text-dark fs-6'>Last name</label>
          <input
            placeholder='Last name'
            type='text'
            autoComplete='off'
            {...formik.getFieldProps('lastname')}
            className={clsx(
              'form-control bg-transparent',
              {
                'is-invalid': formik.touched.lastname && formik.errors.lastname,
              },
              {
                'is-valid': formik.touched.lastname && !formik.errors.lastname,
              }
            )}
          />
          {formik.touched.lastname && formik.errors.lastname && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.lastname}</span>
              </div>
            </div>
          )}
        </div> */}
                {/* begin::Form group Email */}
                {/* <div className='fv-row mb-5'>
          <label className='form-label fw-semibold text-dark fs-6'>Email</label>
          <input
            placeholder='Email'
            type='email'
            autoComplete='off'
            {...formik.getFieldProps('email')}
            className={clsx(
              'form-control form-control-sm bg-transparent',
              {'is-invalid': formik.touched.email && formik.errors.email},
              {
                'is-valid': formik.touched.email && !formik.errors.email,
              }
            )}
          />
          {formik.touched.email && formik.errors.email && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.email}</span>
              </div>
            </div>
          )}
        </div> */}
                {/* end::Form group */}
                {/* begin::Form group Password */}
                {/* <div className='fv-row mb-5' data-kt-password-meter='true'> */}
                {/* <div className='mb-1'> */}
                {/* <label className='form-label fw-semibold text-dark fs-6'>Password</label>
            <div className='position-relative mb-3'>
              <input
                type='password'
                placeholder='Password'
                autoComplete='off'
                {...formik.getFieldProps('password')}
                className={clsx(
                  'form-control form-control-sm bg-transparent',
                  {
                    'is-invalid': formik.touched.password && formik.errors.password,
                  },
                  {
                    'is-valid': formik.touched.password && !formik.errors.password,
                  }
                )}
              />
              {formik.touched.password && formik.errors.password && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.password}</span>
                  </div>
                </div>
              )}
            </div> */}
                {/* begin::Meter */}
                {/* <div
              className='d-flex align-items-center mb-3'
              data-kt-password-meter-control='highlight'
            >
              <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
              <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
              <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
              <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px'></div>
            </div> */}
                {/* end::Meter */}
                {/* </div> */}
                {/* <div className='text-gray-700 fs-7'>
            Use 8 or more characters with a mix of letters, numbers & symbols.
          </div> */}
                {/* </div> */}
                {/* end::Form group */}
                {/* begin::Form group Confirm password */}
                {/* <div className='fv-row'>
          <label className='form-label fw-semibold text-dark fs-6'>Confirm Password</label>
          <input
            type='password'
            placeholder='Password confirmation'
            autoComplete='off'
            {...formik.getFieldProps('changepassword')}
            className={clsx(
              'form-control form-control-sm bg-transparent',
              {
                'is-invalid': formik.touched.changepassword && formik.errors.changepassword,
              },
              {
                'is-valid': formik.touched.changepassword && !formik.errors.changepassword,
              }
            )}
          />
          {formik.touched.changepassword && formik.errors.changepassword && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.changepassword}</span>
              </div>
            </div>
          )}
        </div> */}
                {/* end::Form group */}
                {/* <div className='fv-row mb-5'>
          <label className='form-label fw-bolder text-dark fs-6'>Access Code</label>
          <input
            placeholder='Access code'
            type='text'
            autoComplete='off'
            {...formik.getFieldProps('accessCode')}
            className={clsx(
              'form-control bg-transparent',
              {
                'is-invalid': formik.touched.accessCode && formik.errors.accessCode,
              },
              {
                'is-valid': formik.touched.accessCode && !formik.errors.accessCode,
              }
            )}
          />
          {formik.touched.accessCode && formik.errors.accessCode && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.accessCode}</span>
              </div>
            </div>
          )}
        </div>

        <div className='fv-row mb-5'>
          <label className='form-label fw-bolder text-dark fs-6'>Referral ID (Optional)</label>
          <input
            placeholder='Referral Id'
            type='text'
            autoComplete='off'
            {...formik.getFieldProps('referralId')}
            className={clsx(
              'form-control bg-transparent',
              {
                'is-invalid': formik.touched.referralId && formik.errors.referralId,
              },
              {
                'is-valid': formik.touched.referralId && !formik.errors.referralId,
              }
            )}
          />
          {formik.touched.referralId && formik.errors.referralId && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.referralId}</span>
              </div>
            </div>
          )}
        </div> */}
                {/* begin::Form group */}
                {/* <div className='fv-row my-5'>
          <label className='form-check form-check-inline' htmlFor='kt_login_toc_agree'>
            <input
              className='form-check-input'
              type='checkbox'
              id='kt_login_toc_agree'
              {...formik.getFieldProps('acceptTerms')}
            />
            <span className='fs-7'>
              I agree to SecureSlate's
              <a
                href='https://getsecureslate.com/policy#terms'
                target='_blank'
                className='ms-1 link-primary fw-semibold'
              >
                Terms & Conditions
              </a>{' '}
              and
              <a
                href='https://getsecureslate.com/policy#privacy'
                target='_blank'
                className='ms-1 link-primary fw-semibold'
              >
                Privacy Policy
              </a>
              .
            </span>
          </label>
          {formik.touched.acceptTerms && formik.errors.acceptTerms && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.acceptTerms}</span>
              </div>
            </div>
          )}
        </div> */}
                {/* end::Form group */}
                {/* begin::Form group */}
                {/* end::Form group */}
              </form>
            </div>

            {/* end::Wrapper */}
          </div>
          {/* end::Form */}
          {/* begin::Footer */}
          <div className='d-flex flex-center flex-wrap pt-10 '>
            {/* begin::Links */}
            <div className='d-flex fw-semibold text-primary fs-base'>
              <a
                href='https://getsecureslate.com/policy#terms'
                className='px-5'
                target='_blank'
                rel='noreferrer'
                style={{color: '#31bd93'}}
              >
                Terms
              </a>

              <a
                href='https://getsecureslate.com/pricing'
                className='px-5'
                target='_blank'
                rel='noreferrer'
                style={{color: '#31bd93'}}
              >
                Plans
              </a>

              <a
                href='https://getsecureslate.com/contact'
                className='px-5'
                target='_blank'
                rel='noreferrer'
                style={{color: '#31bd93'}}
              >
                Contact Us
              </a>
            </div>
            {/* end::Links */}
          </div>
          {/* end::Footer */}
        </div>
        {/* end::Body */}

        {/* begin::Aside */}
        <div
          className='d-flex flex-lg-row-fluid w-lg-50 bgi-size-contain bgi-position-center order-1 order-lg-2'
          style={{backgroundColor: '#1f1f1f'}}
        >
          {/* begin::Content */}
          <div className='d-flex flex-column flex-center py-7 py-lg-15 px-5 px-md-15 w-100'>
            {/* begin::Logo */}
            <Link to='/' className='mb-0 mt-6 mb-lg-12'>
              <img
                alt='Logo'
                src={toAbsoluteUrl('/Secureslate_logos/logo.png')}
                className='h-50px'
              />
            </Link>
            {/* end::Logo */}
            {/* begin::Image */}
            <img
              className='mx-auto w-275px w-md-50 w-xl-500px mb-10 mb-lg-20 d-none d-sm-block'
              src={toAbsoluteUrl('/media/svg/illustrations/iso-27001-headerimage.svg')}
              alt=''
            />
            {/* end::Image */}-{/* begin::Title */}
            <h3 className='text-white fs-2qx fw-bolder text-center mb-7 d-none d-sm-block'>
              The world's most cutting-edge platform for automating compliance
            </h3>
            {/* end::Title */}
            {/* begin::Text */}
            <div className='fs-lg-3 text-gray-400 d-none d-sm-block'>
              ISO 27001, SOC 2, PCI DSS, HIPAA, GDPR, CCPA, and more.
            </div>
            {/* end::Text */}
          </div>
          {/* end::Content */}
        </div>
        {/* end::Aside */}
      </div>
      <ToastContainer />
    </>
  )
}
